import { useEffect, useState } from "react";
import type { Ordre } from "types/ordre";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";
import { vehiclesApi } from "@services/api/vehicles";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";
import ReservationModal from "@components/modals/ReservationModal";

/**
 * Opens the flotte "Modifier la réservation" modal for the loaner reservation
 * currently attached to an ordre (ordre.rental_reservation_id). Fetches the
 * reservation + fleet on mount, then reuses ReservationModal so the loaner-desk
 * and the ordre actions menu edit the same way. Keeps the ordre's
 * rental_vehicle_id in sync when the vehicle is swapped, and clears the loaner
 * link on delete.
 */
export default function EditRentalReservationModal({
  ordre,
  onClose,
  onChanged,
}: {
  ordre: Ordre;
  onClose: () => void;
  onChanged?: () => void;
}) {
  const orgId = useDataStore((s) => s.orgData?.id);
  const [reservation, setReservation] = useState<VehicleReservation | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    const resId = ordre.rental_reservation_id;
    if (!resId) { onClose(); return; }
    let alive = true;
    Promise.all([
      vehiclesApi.getReservation(resId),
      orgId ? vehiclesApi.list({ org_id: orgId, limit: 500 }) : Promise.resolve([]),
    ])
      .then(([res, list]) => {
        if (!alive) return;
        setReservation(res);
        setVehicles(list);
      })
      .catch(() => {
        if (!alive) return;
        showNotification({ type: "error", message: "Réservation introuvable" });
        onClose();
      });
    return () => { alive = false; };
  }, [ordre.rental_reservation_id, orgId]);

  if (!reservation) return null;

  return (
    <ReservationModal
      reservation={reservation}
      vehicles={vehicles}
      onClose={onClose}
      onSave={async (updated) => {
        try {
          await vehiclesApi.updateReservation(reservation.id, updated);
          // Keep the ordre pointing at the (possibly swapped) vehicle so the
          // menu chip + planning icon stay accurate.
          if (updated.vehicleId && updated.vehicleId !== ordre.rental_vehicle_id) {
            const updatedOrdre = await ordresApi.update(ordre.id, {
              rental_vehicle_id: updated.vehicleId,
            });
            useOrdreStore.getState().updateOrdre(updatedOrdre);
          }
          showNotification({ type: "success", message: "Réservation modifiée" });
          onChanged?.();
        } catch {
          showNotification({ type: "error", message: "Erreur lors de la modification" });
        }
      }}
      onDelete={async (id) => {
        try {
          await vehiclesApi.deleteReservation(id);
          const updatedOrdre = await ordresApi.update(ordre.id, {
            rental_vehicle_id: "",
            rental_reservation_id: "",
          });
          useOrdreStore.getState().updateOrdre(updatedOrdre);
          showNotification({ type: "success", message: "Véhicule de remplacement retiré" });
          onChanged?.();
        } catch {
          showNotification({ type: "error", message: "Erreur" });
        }
      }}
    />
  );
}
